import React from 'react';
import { ViewType, AppLanguage, UserProfile, ChatbotPersonality, AppTheme } from '../types';
import { translations } from '../services/translations';
import { 
  MessageSquare, 
  ImageIcon as ImageIconIcon, 
  Video, 
  Music, 
  MapPin, 
  Search,
  Book,
  Languages,
  Sparkles,
  History,
  X,
  Settings
} from 'lucide-react';

interface SidebarProps {
  currentView: ViewType;
  onViewChange: (view: ViewType) => void;
  isPro: boolean;
  creditsUsed: number;
  language: AppLanguage;
  onLanguageChange: (lang: AppLanguage) => void;
  user: UserProfile;
  onSignOut: () => void;
  isOpen: boolean;
  onToggle: () => void;
  searchHistory: string[];
  onHistoryClick: (query: string) => void;
  onPersonalityChange: (persona: ChatbotPersonality) => void;
  onThemeChange: (theme: AppTheme) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  currentView, 
  onViewChange, 
  user,
  language,
  isOpen,
  onToggle,
  searchHistory,
  onHistoryClick
}) => {
  const t = translations[language].sidebar;

  const menuItems = [
    { type: ViewType.CHAT, icon: <MessageSquare size={18} />, label: t.chat },
    { type: ViewType.DICTIONARY, icon: <Book size={18} />, label: t.dictionary },
    { type: ViewType.TRANSLATE, icon: <Languages size={18} />, label: t.translate },
    { type: ViewType.SEARCH, icon: <Search size={18} />, label: t.search },
    { type: ViewType.MAPS, icon: <MapPin size={18} />, label: t.maps },
    { type: ViewType.IMAGE, icon: <ImageIconIcon size={18} />, label: t.image },
    { type: ViewType.VIDEO, icon: <Video size={18} />, label: t.video },
    { type: ViewType.AUDIO, icon: <Music size={18} />, label: t.audio },
  ];

  const handleNav = (view: ViewType) => {
    onViewChange(view);
    if (window.innerWidth < 768) {
      onToggle();
    }
  };

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[45] md:hidden animate-in fade-in duration-300" 
          onClick={onToggle}
        />
      )}

      <div className={`
        fixed md:static inset-y-0 left-0 z-[50] 
        w-[280px] md:w-68 bg-white dark:bg-[#0b0b0b] border-r border-black/5 dark:border-white/5
        flex flex-col pt-8 pb-6 px-4 transition-transform duration-300 cubic-bezier(0.16, 1, 0.3, 1)
        ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        
        <div className="flex items-center justify-between mb-8 px-3">
          <div 
            className="flex items-center gap-3 cursor-pointer group" 
            onClick={() => handleNav(ViewType.CHAT)}
          >
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/10 group-hover:scale-105 transition-transform">
              <Sparkles size={18} className="text-white" fill="currentColor" />
            </div>
            <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-[#e3e3e3]">ZomiGenAI</h2>
          </div>
          <button onClick={onToggle} className="md:hidden p-2 text-slate-400 hover:text-slate-600 dark:hover:text-[#e3e3e3]">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar px-1">
          <nav className="mb-8">
            <div className="space-y-1">
              {menuItems.map((item) => (
                <button
                  key={item.type}
                  onClick={() => handleNav(item.type)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all text-[15px] font-medium ${
                    currentView === item.type 
                    ? 'bg-blue-600/10 text-blue-600 dark:text-[#e3e3e3] font-bold' 
                    : 'text-slate-500 dark:text-[#b4b4b4]/60 hover:bg-slate-50 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-[#e3e3e3]'
                  }`}
                >
                  <div className={currentView === item.type ? 'text-blue-600' : 'opacity-60'}>
                    {item.icon}
                  </div>
                  <span className="truncate">{item.label}</span>
                </button>
              ))}
            </div>
          </nav>

          <section className="mb-6">
            <p className="px-4 text-[10px] font-bold text-slate-300 dark:text-[#b4b4b4]/20 tracking-widest mb-4 flex items-center gap-2 uppercase">
              <History size={12} /> Recent History
            </p>
            <div className="space-y-1">
              {searchHistory.slice(0, 6).map((query, idx) => (
                <button
                  key={idx}
                  onClick={() => onHistoryClick(query)}
                  className="w-full flex items-center gap-3 px-4 py-2 rounded-xl text-xs font-medium text-slate-400 dark:text-[#b4b4b4]/40 hover:text-blue-500 hover:bg-slate-50 dark:hover:bg-white/5 truncate text-left transition-colors"
                >
                  <span className="truncate">{query}</span>
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className="pt-4 border-t border-black/5 dark:border-white/5 space-y-3 px-1">
          <button 
            onClick={() => handleNav(ViewType.PROFILE)}
            className={`flex items-center gap-3 w-full p-3 rounded-2xl border transition-all text-left ${
              currentView === ViewType.PROFILE 
              ? 'bg-blue-600/10 border-blue-600/20' 
              : 'bg-slate-50 dark:bg-white/5 border-black/5 dark:border-white/5 hover:bg-slate-100 dark:hover:bg-white/10'
            }`}
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-700 to-indigo-600 flex items-center justify-center text-sm font-bold text-white shadow-md flex-shrink-0">
              {user.username?.charAt(0) || 'U'}
            </div>
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-[15px] font-bold text-slate-900 dark:text-[#e3e3e3] truncate leading-none mb-1.5">{user.username || 'User'}</span>
              <span className="text-[10px] text-slate-400 dark:text-[#b4b4b4]/40 uppercase font-black tracking-tight">Preferences</span>
            </div>
            <Settings size={18} className={`${currentView === ViewType.PROFILE ? 'text-blue-600' : 'text-slate-300'}`} />
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;